import { useCallback, useEffect } from "react";

import { useWindowDimensions } from "react-native";

import { IntercomWidget } from "@/constants/UI";
import Intercom, { Visibility } from "@/crossPlatform/Intercom";
import useAuth from "@/hooks/useAuth";
import useAppMonitor from "@/services/AppMonitor";
import { WebContentMaxWidth } from "@/ui/layout/ScreenContainer";
import { getResponsiveBottomPadding } from "@/utils/helpCenter.util";
import { isWeb } from "@/utils/platform.util";

// on web, the app's content is centered and capped at WebContentMaxWidth.
// to prevent the intercom widget from overlapping app content, it is hidden when the viewport is too small
const IntercomLauncherMinViewportWidth =
  WebContentMaxWidth +
  // empty space exists on either side of the centered/max-capped app content
  2 * (IntercomWidget.HorizontalPadding + IntercomWidget.Width);

const shouldShowLauncher = ({
  isAuthenticated,
  width,
}: {
  isAuthenticated: boolean;
  width: number;
}) => {
  // on native, there is no intercom widget, and the launcher is always "visible" / allowed to open (on login page pre-auth too)
  if (!isWeb()) return Visibility.VISIBLE;

  // hide launcher if user is not logged in, or if viewport/window is too narrow and widget would overlap content
  return isAuthenticated && width >= IntercomLauncherMinViewportWidth
    ? Visibility.VISIBLE
    : Visibility.GONE;
};

/**
 * Owns the Intercom launcher's on-screen state (bottom padding + visibility).
 *
 * This pushes to a single global Intercom instance, so it MUST be called exactly ONCE  in the entire app (in RootNavigator, next to useIntercomInitializer).
 * otherwise every instance of such a hook would cause the instances to race/overwrite one another, and potentially reset to hiding the intercom widget after a flicker.
 */
const useIntercomLauncherVisibility = (): void => {
  const { captureException } = useAppMonitor();
  const { isAuthenticated } = useAuth();
  const { height, width } = useWindowDimensions();

  const bottomPadding = getResponsiveBottomPadding(height);

  const setUpIntercomInAppMessage = useCallback(async () => {
    try {
      await Intercom.setBottomPadding(bottomPadding);
      const visibility = shouldShowLauncher({ isAuthenticated, width });
      await Intercom.setInAppMessageVisibility(visibility);
    } catch (error) {
      captureException(error);
    }
  }, [bottomPadding, captureException, isAuthenticated, width]);

  useEffect(() => {
    void setUpIntercomInAppMessage();
  }, [setUpIntercomInAppMessage]);
};

export default useIntercomLauncherVisibility;
